import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Instagram, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { resolveImageUrl } from './ImageUpload';
import BookingModal from './BookingModal';
import CardSaveOverlay from './CardSaveOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AthleteSpotlight = () => {
  const [athletes, setAthletes] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/athletes?featured=true&limit=10`),
      axios.get(`${API}/stories?limit=50`),
    ])
      .then(([a, s]) => {
        setAthletes(Array.isArray(a.data) ? a.data : []);
        setStories(Array.isArray(s.data) ? s.data : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Match athlete to story by name inclusion (case-insensitive).
  const storySlugFor = (athleteName) => {
    if (!athleteName) return null;
    const needle = athleteName.toLowerCase().trim();
    const match = stories.find(
      (s) => s.name && s.name.toLowerCase().includes(needle)
    );
    return match ? match.slug : null;
  };

  const scrollBy = (dir) => {
    if (!scrollerRef.current) return;
    const w = scrollerRef.current.offsetWidth;
    scrollerRef.current.scrollBy({ left: dir * w * 0.8, behavior: 'smooth' });
  };

  if (loading) return null;
  if (athletes.length === 0) return null;

  return (
    <section className="relative bg-black text-white py-20 lg:py-28 overflow-hidden" data-testid="athlete-spotlight-section">
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 10% 30%, rgba(74,158,221,0.35), transparent 55%), radial-gradient(circle at 90% 70%, rgba(96,165,250,0.25), transparent 55%)'
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 lg:mb-14">
          <div className="max-w-2xl">
            <p className="font-italic text-blue-300 text-sm mb-3 tracking-wider uppercase">Inside Their World</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]">
              The athletes we&apos;re <span className="blue-gradient">telling now</span>
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-3" data-testid="spotlight-arrows">
            <button
              onClick={() => scrollBy(-1)}
              className="w-11 h-11 rounded-full border border-white/30 hover:bg-white hover:text-black flex items-center justify-center transition"
              aria-label="Previous athlete"
              data-testid="spotlight-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="w-11 h-11 rounded-full border border-white/30 hover:bg-white hover:text-black flex items-center justify-center transition"
              aria-label="Next athlete"
              data-testid="spotlight-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Horizontal scroller */}
        <div
          ref={scrollerRef}
          className="flex gap-5 lg:gap-7 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
        >
          {athletes.map((a, idx) => {
            const slug = storySlugFor(a.name);
            const target = slug ? `/stories/${slug}` : '/stories';
            return (
              <div key={a.id} className="relative flex-shrink-0 w-[78%] sm:w-[46%] md:w-[34%] lg:w-[28%] snap-start">
                <Link
                  to={target}
                  className="relative group block"
                  data-testid={`spotlight-card-${idx}`}
                >
                  <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
                    <img loading="lazy" decoding="async"
                      src={resolveImageUrl(a.photo, 400) || 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800'}
                      alt={a.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] uppercase tracking-widest font-bold border border-white/30">
                        {a.sport}
                      </span>
                    </div>

                    <div className="absolute inset-x-0 bottom-0 p-5 lg:p-6">
                      <p className="font-italic text-blue-300 text-xs mb-1.5">{a.team}</p>
                      <h3 className="font-display text-2xl lg:text-3xl uppercase leading-tight mb-2">{a.name}</h3>
                      {a.bio && <p className="font-body text-sm text-gray-200 line-clamp-2 mb-3 max-w-[95%]">{a.bio}</p>}
                      <div className="flex items-center justify-between">
                        {a.instagram && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-200 font-bold">
                            <Instagram className="w-3.5 h-3.5" /> {a.instagram}
                          </span>
                        )}
                        <span
                          className="text-xs font-bold inline-flex items-center gap-1 text-blue-300 group-hover:text-white transition"
                          data-testid={slug ? `spotlight-read-story-${idx}` : `spotlight-see-stories-${idx}`}
                        >
                          {slug ? 'Get Access' : 'Step Inside'} <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
                <CardSaveOverlay type="athletes" id={a.id} position="top-right" />
              </div>
            );
          })}

          {/* End-of-rail "Apply" card — opens BookingModal */}
          <button
            type="button"
            onClick={() => setBookingOpen(true)}
            className="relative flex-shrink-0 w-[78%] sm:w-[46%] md:w-[34%] lg:w-[28%] snap-start group text-left"
            data-testid="spotlight-apply-card"
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500/30 via-black to-black border border-white/20 flex flex-col items-center justify-center text-center p-8 hover:border-white/50 transition">
              <p className="font-italic text-blue-300 text-xs mb-3 uppercase tracking-widest">We&apos;re booking</p>
              <h3 className="font-display text-3xl uppercase leading-tight mb-3">
                Be the <span className="blue-gradient">next</span> story
              </h3>
              <p className="font-body text-sm text-gray-300 mb-6 max-w-xs">
                Athletes at every level — high school, college, pro. We tell the real story.
              </p>
              <span className="btn-pill bg-white text-black text-sm group-hover:bg-blue-400 group-hover:text-white transition inline-flex items-center gap-2">
                Apply Now <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </button>

          {/* iter311 · Ambassador funnel card — sits next to "Be the next
              story" so anyone browsing the athlete rail also sees the
              "help build FLYT" ask. Amber-glass to mirror the ambassador
              pill on the profile, so the visual thread is consistent. */}
          <Link
            to="/ambassador-signup"
            className="relative flex-shrink-0 w-[78%] sm:w-[46%] md:w-[34%] lg:w-[28%] snap-start group"
            data-testid="spotlight-ambassador-card"
          >
            <div
              className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-amber-300/30 flex flex-col items-center justify-center text-center p-8 hover:border-amber-300/70 transition"
              style={{ background: 'linear-gradient(135deg, rgba(250,204,21,0.28), rgba(0,0,0,0.9) 55%, #000)' }}
            >
              <p className="font-italic text-amber-300 text-xs mb-3 uppercase tracking-widest">Ambassador Program</p>
              <h3 className="font-display text-3xl uppercase leading-tight mb-3">
                Become a <span className="text-amber-300">FLYT</span> ambassador
              </h3>
              <p className="font-body text-sm text-gray-300 mb-6 max-w-xs">
                Athletes, agents, creators, connectors — help build the Athlete Career OS and earn tier + status.
              </p>
              <span className="btn-pill bg-amber-300 text-black text-sm group-hover:bg-amber-200 transition inline-flex items-center gap-2">
                Apply Now <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>

        <div className="mt-8 text-center">
          <Link to="/athletes" className="font-body font-bold text-sm text-blue-300 hover:text-white inline-flex items-center gap-1" data-testid="see-all-athletes">
            See the full roster <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} presetCategory="All" />
    </section>
  );
};

export default AthleteSpotlight;
